import { FileWarning, Percent } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { InfoNote } from '@/components/common/Panel'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { fmtDate } from '../../shared/format'
import type { TaxReport, Transaction } from '../types'
import { useFinanceLookups } from '../ui'
import { Money, ReviewBadge, Section, VatStatusBadge } from './FinanceBits'

/** KDV açısından değerlendirilen gider kalemleri: dar alanda kart, genişte tablo. */
export function VatRows({ report }: { report: TaxReport }) {
  const { t } = useTranslation()
  const { tx, catName, partyName } = useFinanceLookups()
  const rows = report.vatRows.map((r) => ({ ...r, tx: tx.get(r.transactionId) })).filter((r): r is typeof r & { tx: Transaction } => !!r.tx)
  return (
    <Section title={t('finance.tax.vatRowsTitle')} bodyClassName="p-0">
      <p className="border-b px-4 py-2 text-xs text-muted-foreground">{t('finance.tax.vatRowsHint')}</p>
      <div className="@container">
        <ul className="divide-y @3xl:hidden">
          {rows.map((r) => (
            <li key={r.transactionId} className="space-y-1.5 p-4">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{r.tx.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {fmtDate(r.tx.date)} · {catName(r.tx.categoryId)}
                  </p>
                </div>
                <VatStatusBadge status={r.vatStatus} />
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('finance.tx.net')}: <Money value={r.tx.net} />
                </span>
                <span>
                  {t('finance.tx.vat')}: <Money value={r.tx.vat} className="font-medium" />
                </span>
              </div>
            </li>
          ))}
        </ul>
        <div className="hidden @3xl:block">
          <Table>
            <THead>
              <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
                <TH>{t('finance.tx.date')}</TH>
                <TH>{t('finance.tx.description')}</TH>
                <TH className="hidden @4xl:table-cell">{t('finance.category')}</TH>
                <TH className="text-right">{t('finance.tx.net')}</TH>
                <TH className="text-right">{t('finance.tx.vat')}</TH>
                <TH>{t('finance.tax.vatStatusCol')}</TH>
                <TH className="hidden @5xl:table-cell">{t('finance.tax.note')}</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((r) => (
                <TR key={r.transactionId}>
                  <TD className="whitespace-nowrap text-sm tabular-nums">{fmtDate(r.tx.date)}</TD>
                  <TD className="max-w-60">
                    <p className="truncate font-medium">{r.tx.description}</p>
                    <p className="truncate text-xs text-muted-foreground">{partyName(r.tx.counterpartyId)}</p>
                  </TD>
                  <TD className="hidden text-sm @4xl:table-cell">{catName(r.tx.categoryId)}</TD>
                  <TD className="text-right"><Money value={r.tx.net} /></TD>
                  <TD className="text-right"><Money value={r.tx.vat} /></TD>
                  <TD><VatStatusBadge status={r.vatStatus} /></TD>
                  <TD className="hidden text-xs text-muted-foreground @5xl:table-cell">{t(`finance.vatStatusHint.${r.vatStatus}`)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </div>
    </Section>
  )
}

export function WithholdingTab({ report }: { report: TaxReport }) {
  const { t } = useTranslation()
  const { tx, partyName } = useFinanceLookups()
  return (
    <Section title={t('finance.tax.withholdingTitle')} icon={Percent} actions={<Money value={report.withholding.total} className="font-semibold" />} bodyClassName="p-0">
      {report.withholding.rows.length === 0 ? (
        <p className="p-6 text-center text-sm text-muted-foreground">{t('finance.tax.noWithholding')}</p>
      ) : (
        <ul className="divide-y">
          {report.withholding.rows.map((r) => {
            const x = tx.get(r.transactionId)
            return (
              <li key={r.transactionId} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
                <div className="min-w-0 flex-1 basis-48">
                  <p className="truncate font-medium">{x ? partyName(x.counterpartyId) : '—'}</p>
                  <p className="text-xs text-muted-foreground">
                    {x?.description} · %{x?.withholdingRate}
                  </p>
                </div>
                <span className="text-sm text-muted-foreground">
                  {t('finance.tax.base')}: <Money value={r.base} />
                </span>
                <Money value={r.amount} className="font-semibold" />
              </li>
            )
          })}
        </ul>
      )}
    </Section>
  )
}

export function CorporateTab({ report, regime = 'corporate' }: { report: TaxReport; regime?: 'corporate' | 'income' }) {
  const { t } = useTranslation()
  const c = report.corporate
  const income = regime === 'income'
  const items: [string, string][] = [
    [t('finance.tax.ytdIncome'), c.ytdIncome],
    [t('finance.tax.ytdExpense'), c.ytdExpense],
    [t('finance.tax.corporateBase'), c.base],
    [income ? t('finance.tax.incomeEstimate') : t('finance.tax.corporateEstimate'), c.estimate],
  ]
  const details: [string, string][] = [
    [t('finance.tax.interestIncome'), c.interestIncome],
    [t('finance.tax.grossTax', { rate: c.rate || '—' }), c.gross],
    [t('finance.tax.prepaidTax'), c.prepaidTax],
  ]
  return (
    <Section title={income ? t('finance.tax.incomeTitle') : t('finance.tax.corporateTitle')}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {items.map(([label, value], i) => (
          <div key={label} className={i === 3 ? 'rounded-lg bg-primary/[0.06] p-3' : 'rounded-lg bg-muted/40 p-3'}>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-lg font-semibold"><Money value={value} /></p>
          </div>
        ))}
      </div>
      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
        {details.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-2 border-b py-1.5">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="font-medium"><Money value={value} /></dd>
          </div>
        ))}
      </dl>
      <InfoNote className="mt-4 p-3">{income ? t('finance.tax.incomeHint') : t('finance.tax.corporateHint')}</InfoNote>
    </Section>
  )
}

export function DocCheckTab({ report, month }: { report: TaxReport; month: string }) {
  const { t } = useTranslation()
  const { tx, partyName } = useFinanceLookups()
  const list = [...tx.values()].filter((x) => x.date.startsWith(month) && (!x.hasDocument || x.review !== 'approved'))
  return (
    <Section
      title={t('finance.tax.docCheckTitle')}
      icon={FileWarning}
      bodyClassName="p-0"
      actions={
        <Link to={`/finance/transactions?status=missing`} className="text-xs font-medium text-primary hover:underline">
          {t('finance.tax.goToRecords')}
        </Link>
      }
    >
      <div className="grid grid-cols-1 gap-3 border-b p-4 sm:grid-cols-3">
        <div className="rounded-lg bg-destructive/[0.06] p-3">
          <p className="text-xs text-muted-foreground">{t('finance.tax.missingDocs')}</p>
          <p className="text-lg font-semibold text-destructive">{report.docCheck.missing}</p>
        </div>
        <div className="rounded-lg bg-destructive/[0.06] p-3">
          <p className="text-xs text-muted-foreground">{t('finance.tax.missingAmount')}</p>
          <p className="text-lg font-semibold"><Money value={report.docCheck.missingAmount} /></p>
        </div>
        <div className="rounded-lg bg-warning/10 p-3">
          <p className="text-xs text-muted-foreground">{t('finance.tax.unreviewed')}</p>
          <p className="text-lg font-semibold">{report.docCheck.unreviewed}</p>
        </div>
      </div>
      <ul className="divide-y">
        {list.map((x) => (
          <li key={x.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
            <div className="min-w-0 flex-1 basis-48">
              <p className="truncate font-medium">{x.description}</p>
              <p className="text-xs text-muted-foreground">
                {fmtDate(x.date)} · {partyName(x.counterpartyId)}
              </p>
            </div>
            {!x.hasDocument ? <span className="text-xs font-medium text-destructive">{t('finance.tx.missingDocShort')}</span> : null}
            <ReviewBadge status={x.review} />
            <Money value={x.total} className="text-sm font-medium" />
          </li>
        ))}
      </ul>
    </Section>
  )
}
